import React from 'react';
import { cn } from '@/lib/utils';

type Variant = 'primary' | 'secondary' | 'muted' | 'outline' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary: 'bg-blue-500 text-white hover:bg-blue-600 active:scale-95',
  secondary: 'bg-emerald-500 text-white hover:bg-emerald-600 active:scale-95',
  muted: 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:scale-95',
  outline: 'border-2 border-gray-200 text-gray-700 hover:bg-gray-50 active:scale-95',
  ghost: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 active:scale-95',
  danger: 'border-2 border-gray-200 text-gray-700 hover:bg-gray-100 active:scale-95',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading, disabled, className, children, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center font-semibold rounded-md transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none hover:scale-105',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
);

Button.displayName = 'FlatButton';

export { Button };
export type { ButtonProps, Variant as ButtonVariant, Size as ButtonSize };
